import { useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";

const DRAFT_KEY = "hticu_application_draft";
const DRAFT_EXPIRY_DAYS = 30;

interface DraftData {
  formData: any;
  timestamp: number;
  currentStep: number;
}

export function useApplicationAutosave() {
  const { toast } = useToast();

  const saveDraft = useCallback((formData: any, currentStep: number) => {
    try {
      const draft: DraftData = {
        formData,
        timestamp: Date.now(),
        currentStep,
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      console.log("[AutoSave] Draft saved successfully", { currentStep });
    } catch (error) {
      console.error("[AutoSave] Failed to save draft:", error);
    }
  }, []);

  const restoreDraft = useCallback((): DraftData | null => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return null;

      const draft: DraftData = JSON.parse(saved);
      
      // Check if draft is expired
      const daysSinceCreation = (Date.now() - draft.timestamp) / (1000 * 60 * 60 * 24);
      if (daysSinceCreation > DRAFT_EXPIRY_DAYS) {
        console.log("[AutoSave] Draft expired, clearing");
        clearDraft();
        return null;
      }

      console.log("[AutoSave] Draft restored", { currentStep: draft.currentStep });
      return draft;
    } catch (error) {
      console.error("[AutoSave] Failed to restore draft:", error);
      return null;
    }
  }, []);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      console.log("[AutoSave] Draft cleared");
    } catch (error) {
      console.error("[AutoSave] Failed to clear draft:", error);
    }
  }, []);

  const hasDraft = useCallback((): boolean => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved !== null;
    } catch {
      return false;
    }
  }, []);

  return {
    saveDraft,
    restoreDraft,
    clearDraft,
    hasDraft,
  };
}
